import React from 'react'
import { localized } from '@/decorators/localized'
import ReportingGroupDetailFormContent from './reportingGroupDetailFormContent'
import { WhiteBox, ButtonSection } from '@/components/Layout'
import { Button } from 'reactstrap'
import { BooleanColumn, BsrsGrid, Column, commandColumnProps, DateCell, DateFilterCell } from '@/components/BsrsGrid'
import Icon from '@/components/Icon'
import AddNewReportSubmitter from './addNewReportSubmitter'
import context from '@/context'
import { SubmitterType } from '@/enums/SubmitterType'
import { ReportingGroupMemberType } from '@/enums/ReportingGroupMemberType'
import { submitterActionsCell } from './reportingGroupDetail.ActionButtonsCell'
import { ValidationBoundary, ValidationState } from '@/components/validation'
import { dialogTypes } from '@/dialogs'
import { TabNavItems, TabContent, TabPane } from '@/components/tabs'
import { withPermissions } from '@/decorators/withPermissions'

@localized()
@withPermissions()
class ReportingGroupDetailForm extends React.Component {
  constructor (props, context) {
    super(props, context)

    this.state = {
      ...props.reportingGroup,
      showHideFilter: false,
      activeTab: '1'
    }
    this.toggle = this.toggle.bind(this)
  }
  canManage = () => this.props.isGranted('CanManageReportGroups')
  save () {
    this.props.update(this.state)
  }
  saveClicked = () => {
    const noWarningNeeded = (
      this.state.warningReportingCycleId === null || // if no parameters exist
      this.state.warningReportingCycleId === this.state.reportingCycle.id // or we are not changing reporting cycle -> no warning
    )
    if (noWarningNeeded) {
      this.save()
    } else {
      this.props.openDialog({
        dialogType: dialogTypes.confirm,
        title: 'Reporting.ReportingGroup',
        content: 'Reporting.SubmissionParametersWarning',
        onAction: () => this.save()
      })
    }
  }
  delete (dataItem) {
    switch (dataItem.submitterType) {
      case SubmitterType.INSTITUTION:
        this.props.removeInstitutionReportSubmitter(this.state.id, dataItem.institutionId)
        break

      case SubmitterType.INSTITUTION_GROUP:
        this.props.excludeInstitutionReportSubmitter({ reportGroupId: this.state.id, id: dataItem.institutionId })
        break

      default:
        return null
    }
  }
  reportSubmittersInstitutionsRowRender (trElement, props) {
    const rowProps = {}
    if (props.dataItem.type === ReportingGroupMemberType.EXCLUDE) {
      rowProps.style = { color: 'rgb(208, 208, 208)' }
    }
    return React.cloneElement(trElement, { ...rowProps }, trElement.props.children)
  }
  canEditDate (dataItem) {
    return this.canManage() && dataItem.submitterType === SubmitterType.INSTITUTION && dataItem.type === ReportingGroupMemberType.MEMBER
  }
  onInstitutionItemEdited (reportGroupId, editedItem) {
    if (this.canEditDate(editedItem.item)) {
      const { institutionId, validFrom, validTo } = editedItem.item
      this.props.saveInstitutionReportSubmitter({
        reportGroupId,
        id: institutionId,
        validFrom,
        validTo
      })
    }
  }
  onInstitutionGroupItemEdited (reportGroupId, editedItem) {
    const { institutionGroupId, validFrom, validTo } = editedItem.item
    this.props.saveInstitutionGroupReportSubmitter({
      reportGroupId,
      id: institutionGroupId,
      validFrom,
      validTo
    })
  }
  openCycleParameters (reportGroupId, dataItem) {
    this.props.openDialog({
      dialogType: dialogTypes.editCycleParameters,
      reportGroupId,
      reportingCycleSubmissionId: dataItem.reportingCycleSubmissionId,
      localize: this.props.localize,
      disabled: !this.canManage(),
      onAction: () => this.props.onParametersSaved()
    })
  }
  toggle (tab) {
    if (this.state.activeTab !== tab) {
      this.setState({
        activeTab: tab
      })
    }
  }
  render () {
    const { localize, localizeProps, reportSubmitters } = this.props
    const reportGroupId = this.state.id || 0
    const canManage = this.canManage()
    return (
      <React.Fragment>
        <WhiteBox>
          <React.Fragment>
            <TabNavItems
              activeTabId={this.state.activeTab}
              onClick={tabId => this.toggle(tabId)}
              items={[
                { id: '1', text: 'Reporting.General' },
                { id: '2', text: 'Reporting.ReportSubmitters' },
                { id: '3', text: 'Reporting.ReportGroupCycleParametersTitle' }
              ]} />
            <TabContent activeTab={this.state.activeTab}>
              <TabPane tabId='1'>
                <ValidationBoundary>
                  <ReportingGroupDetailFormContent
                    {...this.state}
                    onChange={value => this.setState(value)}
                    localizeProps={localizeProps}
                    showState
                    localize={localize}>
                    <ValidationState render={({ isValid }) =>
                      <ButtonSection>
                        <Button className='m-l-a' color='success' disabled={!isValid || !canManage} onClick={this.saveClicked}><Icon name='save' /> <span>{localize('Common.Save')}</span></Button>
                      </ButtonSection>
                    } />
                  </ReportingGroupDetailFormContent>
                </ValidationBoundary>
              </TabPane>
              <TabPane tabId='2'>
                <div className='box__title'>
                  {localize('Reporting.ReportSubmittersInstitutions')}
                </div>
                <BsrsGrid
                  editable={canManage}
                  exitEditingOnEnter
                  dataSource={request => context.api.reportGroupsService.getAllSubmittersTableData(reportGroupId, request)}
                  rowRender={this.reportSubmittersInstitutionsRowRender}
                  canEditField={(field, dataItem) => this.canEditDate(dataItem)}
                  onItemEdited={(editedItem) => this.onInstitutionItemEdited(reportGroupId, editedItem)}
                  lastReloadedAt={this.props.reportSubmitters.reportSubmittersLastReloadedAt}>

                  <Column field='institutionName' title={localize('Common.InstitutionName')} editable={false} />
                  <Column field='institutionLatinName' title={localize('Common.InstitutionLatinName')} editable={false} />
                  <Column field='institutionGroupName' title={localize('Common.InstitutionGroupName')} editable={false} />
                  <Column field='validFrom' title={localize('Reporting.ValidFrom')} filter='date' cell={DateCell} filterCell={DateFilterCell} editor='date' editable />
                  <Column field='validTo' title={localize('Reporting.ValidTo')} filter='date' cell={DateCell} filterCell={DateFilterCell} editor='date' editable />
                  <Column {...submitterActionsCell({ disabled: !this.canManage(), onClick: dataItem => this.delete(dataItem), localize })} />
                </BsrsGrid>
                {canManage && <AddNewReportSubmitter
                  reportGroupId={reportGroupId}
                  dataSource={context.api.institutionService.getLookup}
                  reportSubmitters={reportSubmitters}
                  addReportSubmitter={dto => this.props.saveInstitutionReportSubmitter(dto)}
                  isGroupSubmitter={false}
                />}
                <div className='box__title'>
                  {localize('Reporting.ReportSubmittersInstitutionGroups')}
                </div>
                <BsrsGrid
                  editable={canManage}
                  exitEditingOnEnter
                  onItemEdited={(editedItem) => this.onInstitutionGroupItemEdited(reportGroupId, editedItem)}
                  dataSource={request => context.api.reportGroupsService.getAllInstitutionGroupsTableData(reportGroupId, request)}
                  lastReloadedAt={this.props.reportSubmitters.reportSubmittersLastReloadedAt}>

                  <Column field='institutionGroupName' title={localize('Common.InstitutionGroupName')} editable={false} />
                  <Column field='validFrom' title={localize('Reporting.ValidFrom')} filter='date' cell={DateCell} filterCell={DateFilterCell} editor='date' editable />
                  <Column field='validTo' title={localize('Reporting.ValidTo')} filter='date' cell={DateCell} filterCell={DateFilterCell} editor='date' editable />
                  <Column {...commandColumnProps({
                    deleteAction: dataItem => this.props.removeInstitutionGroupReportSubmitter(reportGroupId, dataItem.institutionGroupId),
                    deleteTitleKey: 'Reporting.RemoveInstitutionGroupTitle',
                    deleteContentKey: 'Reporting.RemoveInstitutionGroupContent',
                    localize,
                    deleteDisabled: !canManage
                  })} />
                </BsrsGrid>
                {canManage && <AddNewReportSubmitter
                  reportGroupId={reportGroupId}
                  dataSource={context.api.institutionGroupService.getLookup}
                  reportSubmitters={reportSubmitters}
                  addReportSubmitter={dto => this.props.saveInstitutionGroupReportSubmitter(dto)}
                  isGroupSubmitter
                />}
              </TabPane>
              <TabPane tabId='3'>
                <BsrsGrid
                  dataSource={request => context.api.reportGroupCycleParameterService.getAllTableData(reportGroupId, request)}>
                  <Column field='order' title={localize('Common.Order')} filter='numeric' />
                  <Column field='snapshotDate' title={localize('Reporting.SnapshotDate')} filter='date' cell={DateCell} filterCell={DateFilterCell} editor='date' />
                  <Column field='dueDate' title={localize('Reporting.DueDate')} filter='date' cell={DateCell} filterCell={DateFilterCell} editor='date' />
                  <Column field='extendedDueDate' title={localize('Reporting.ExtendedDueDate')} filter='date' cell={DateCell} filterCell={DateFilterCell} editor='date' />
                  {BooleanColumn({ field: 'isActive', title: localize('Common.IsActive') })}
                  {BooleanColumn({ field: 'hasParameters', title: localize('Reporting.HasParameters') })}
                  <Column {...commandColumnProps({ deleteDisabled: !canManage, onDetailClick: dataItem => this.openCycleParameters(reportGroupId, dataItem) })} />
                </BsrsGrid>
              </TabPane>
            </TabContent>
          </React.Fragment>
        </WhiteBox>
      </React.Fragment>
    )
  }
}

export default ReportingGroupDetailForm



// WEBPACK FOOTER //
// ./src/internal-app/pages/reportingGroups/reportingGroupDetailForm.js